import React from "react";
import { useImmerReducer } from "use-immer";

import { doNothing } from "@examspark/common/dist/utils";
import { Resource } from "@examspark/types";

export type BrowserAction =
  | {
      type: "TOGGLE_SELECTED";
      data: Resource;
    }
  | {
      type: "SET_SELECTED";
      data: Resource[];
      readOnly?: boolean;
    }
  | {
      type: "ADD_SELECTED";
      data: Resource[];
    }
  | {
      type: "REMOVE_SELECTED";
      data: Resource[];
    }
  | {
      type: "CLEAR_SELECTED";
    }
  | {
      type: "SET_FOCUSED";
      data: Resource;
    }
  | {
      type: "SET_PREDICATES";
      data: Partial<Pick<BrowserState, "canSelect" | "canDeselect">>;
    }
  | {
      type: "CLEAR_FOCUSED";
    }
  | {
      type: "TOGGLE_FILTERS";
    }
  | {
      type: "SHOW_FILTERS";
    }
  | {
      type: "HIDE_FILTERS";
    };

export const browserReducer = (state: BrowserState, action: BrowserAction) => {
  const canSelect = (item: Resource) => state.canSelect?.(item) !== false;
  const canDeselect = (item: Resource) => state.canDeselect?.(item) !== false;

  switch (action.type) {
    case "TOGGLE_SELECTED": {
      const toggleSelectedFindIndex = state.selected.findIndex(
        (item) => item.id === action.data.id,
      );

      if (toggleSelectedFindIndex === -1) {
        if (!canSelect(action.data)) return;
        state.selected.push(action.data);
      } else {
        if (!canDeselect(action.data)) return;
        state.selected.splice(toggleSelectedFindIndex, 1);
      }
      break;
    }
    case "SET_SELECTED":
      if (action.readOnly) {
        state.selectedReadOnly = action.data.filter(canSelect);
      } else {
        state.selected = action.data.filter(canSelect);
      }
      break;
    case "CLEAR_SELECTED":
      state.selected = state.selected.filter((item) => !canDeselect(item));
      break;
    case "SET_FOCUSED":
      state.focused = action.data.id;
      break;
    case "ADD_SELECTED":
      action.data.forEach((item) => {
        const findIndex = state.selected.findIndex(
          (selectedItem) => selectedItem.id === item.id,
        );
        if (findIndex === -1 && canSelect(item)) {
          state.selected.push(item);
        }
      });
      break;
    case "REMOVE_SELECTED":
      action.data.forEach((item) => {
        const findIndex = state.selected.findIndex(
          (selectedItem) => selectedItem.id === item.id,
        );
        if (findIndex !== -1 && canDeselect(item)) {
          state.selected.splice(findIndex, 1);
        }
      });
      break;
    case "CLEAR_FOCUSED":
      state.focused = undefined;
      break;
    case "TOGGLE_FILTERS":
      state.showFilters = !state.showFilters;
      break;
    case "HIDE_FILTERS":
      state.showFilters = false;
      break;
    case "SHOW_FILTERS":
      state.showFilters = true;
      break;
    case "SET_PREDICATES":
      state.canSelect = action.data.canSelect ?? (() => true);
      state.canDeselect = action.data.canDeselect ?? (() => true);
      break;
  }

  return state;
};

export const defaultBrowserState: BrowserState = {
  selected: [],
  selectedReadOnly: [],
  focused: undefined,
  showFilters: true,
  canSelect: () => true,
  canDeselect: () => true,
};

export interface BrowserState {
  selected: Resource[];
  selectedReadOnly: Resource[];
  focused?: string;
  showFilters?: boolean;
  canSelect: (resource: Resource) => boolean;
  canDeselect: (resource: Resource) => boolean;
}

/** Contains selected item and focused item */
export const BrowserContext = React.createContext<
  [BrowserState, (params: BrowserAction) => void]
>([defaultBrowserState, doNothing]);

/**
 * Wrap all content browsers with this provider to:
 * - keep track of which items are selected
 * - keep track of which item is in focus.
 */
const BrowserContextProvider: React.FC<React.PropsWithChildren<unknown>> = (
  props,
) => {
  const [browserState, dispatchBrowser] = useImmerReducer<
    BrowserState,
    BrowserAction
  >(browserReducer, defaultBrowserState);

  return (
    <BrowserContext.Provider value={[browserState, dispatchBrowser]}>
      {props.children}
    </BrowserContext.Provider>
  );
};

export default BrowserContextProvider;
